import DisabledButton from 'components/elements/buttons/DisabledButton'
import PrimaryButton from 'components/elements/buttons/PrimaryButton'
import SecondaryButton from 'components/elements/buttons/SecondaryButton'
import {
  DEFAULT_BACK_BUTTON_LABEL,
  DEFAULT_DISABLED_BUTTON_LABEL,
  DEFAULT_FORWARD_BUTTON_LABEL,
} from 'constants/label'
import { HistoryStackContext } from 'contexts/HistoryStackContext'
import React, { useCallback, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { transitionWithAction } from 'utils/commonUtils'
import { isSjDomain , isKsDomain } from 'utils/domain'
import { fail, StatusProps } from 'utils/status'

/**
 * 画面下部の画面遷移用ボタン表示エリア
 */
const ActionArea: React.FC<ActionAreaProps> = (props) => {
  const history = useHistory()
  const historyStackContext = useContext(HistoryStackContext)

  const isNormal = (item: ActionAreaProps): item is ForwardActionAreaProps =>
    !item.hideForward

  const goNext = useCallback(async (): Promise<StatusProps> => {
    if (isNormal(props)) {
      return await transitionWithAction(
        historyStackContext,
        props.forwardLabel ? props.forwardLabel : DEFAULT_FORWARD_BUTTON_LABEL,
        () => {
          history.push(props.forwardTo)
        },
        props.forwardAction
      )
    } else {
      return fail()
    }
  }, [props.forwardLabel, props.forwardAction, props.forwardTo])

  const goNext2 = useCallback(async (): Promise<StatusProps> => {
    if (props.forwardTo2) {
      return await transitionWithAction(
        historyStackContext,
        props.forwardLabel2
          ? props.forwardLabel2
          : DEFAULT_FORWARD_BUTTON_LABEL,
        () => {
          if (props.forwardTo2) {
            history.push(props.forwardTo2)
          }
        },
        props.forwardAction2
      )
    } else {
      return fail()
    }
  }, [props.forwardLabel2, props.forwardAction2, props.forwardTo2])

  const goBack = useCallback(async (): Promise<StatusProps> => {
    return await transitionWithAction(
      historyStackContext,
      props.backLabel ? props.backLabel : DEFAULT_BACK_BUTTON_LABEL,
      () => {
        props.backTo ? history.push(props.backTo) : history.goBack()
      },
      props.backAction
    )
  }, [props.backLabel, props.backAction, props.backTo])

  return (
    <StyledActionArea>
      {isNormal(props) &&
        (props.isDisabled ? (
          <DisabledButton withArrow={(isSjDomain() || isKsDomain())}>
            {props.disabledLabel
              ? props.disabledLabel
              : DEFAULT_DISABLED_BUTTON_LABEL}
          </DisabledButton>
        ) : (
          <PrimaryButton onClick={goNext} withArrow={(isSjDomain() || isKsDomain())}>
            {props.forwardLabel
              ? props.forwardLabel
              : DEFAULT_FORWARD_BUTTON_LABEL}
          </PrimaryButton>
        ))}
      {props.forwardLabel2 &&
        (props.isDisabled2 ? (
          <DisabledButton margin="-12px 24px 24px" withArrow={(isSjDomain() || isKsDomain())}>
            {props.disabledLabel2
              ? props.disabledLabel2
              : DEFAULT_DISABLED_BUTTON_LABEL}
          </DisabledButton>
        ) : (
          <PrimaryButton
            margin="-12px 24px 24px"
            onClick={goNext2}
            withArrow={(isSjDomain() || isKsDomain())}
          >
            {props.forwardLabel2
              ? props.forwardLabel2
              : DEFAULT_FORWARD_BUTTON_LABEL}
          </PrimaryButton>
        ))}

      {props.showBack && (
        <SecondaryButton onClick={goBack}>
          {props.backLabel ? props.backLabel : DEFAULT_BACK_BUTTON_LABEL}
        </SecondaryButton>
      )}
    </StyledActionArea>
  )
}

export type ActionAreaProps = {
  forwardLabel?: string
  hideForward?: boolean
  forwardAction?: () => StatusProps | Promise<StatusProps>
  forwardTo?: string
  isDisabled?: boolean
  disabledLabel?: string
  forwardLabel2?: string
  forwardAction2?: () => StatusProps | Promise<StatusProps>
  forwardTo2?: string
  isDisabled2?: boolean
  disabledLabel2?: string
  showBack?: boolean
  backLabel?: string
  backAction?: () => StatusProps | Promise<StatusProps>
  backTo?: string
}

type ForwardActionAreaProps = ActionAreaProps & {
  forwardLabel?: string
  forwardTo: string
}

const StyledActionArea = styled.div`
  padding: 8px;
`

export default ActionArea
